import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { PannableCanvasControls } from './PannableCanvasControls'
import { PannableCanvasContext } from './pannableCanvasContext'
import { usePannableCanvasState, type PannableCanvasTransform } from './usePannableCanvasState'
import { computeEdgeFades, measureLayerContentBounds, type EdgeFades } from './fitToContent'
import type { CanvasAxis, ZoomMode } from '../Dashboard/viewDescriptors'

export type PannableCanvasController = {
  zoomIn: () => void
  zoomOut: () => void
  fitToContent: () => void
  getTransform: () => PannableCanvasTransform
}

export const PannableCanvas = ({
  children,
  viewId,
  workspaceId,
  enabled = true,
  pan = 'xy',
  zoomAxis = 'xy',
  zoomMode,
  onSemanticZoom,
  onControllerReady,
  onTransformChange,
}: {
  children: ReactNode
  viewId: string
  workspaceId: string
  enabled?: boolean
  pan?: CanvasAxis
  zoomAxis?: CanvasAxis
  zoomMode?: ZoomMode
  onSemanticZoom?: (anchorRatio: number, zoomFactor: number) => void
  onControllerReady?: (controller: PannableCanvasController | null) => void
  onTransformChange?: (transform: PannableCanvasTransform) => void
}) => {
  if (!enabled) return <>{children}</>

  return (
    <PannableCanvasInner
      viewId={viewId}
      workspaceId={workspaceId}
      pan={pan}
      zoomAxis={zoomAxis}
      zoomMode={zoomMode}
      onSemanticZoom={onSemanticZoom}
      onControllerReady={onControllerReady}
      onTransformChange={onTransformChange}
    >
      {children}
    </PannableCanvasInner>
  )
}

const PannableCanvasInner = ({
  children,
  viewId,
  workspaceId,
  pan = 'xy',
  zoomAxis = 'xy',
  zoomMode,
  onSemanticZoom,
  onControllerReady,
  onTransformChange,
}: {
  children: ReactNode
  viewId: string
  workspaceId: string
  pan?: CanvasAxis
  zoomAxis?: CanvasAxis
  zoomMode?: ZoomMode
  onSemanticZoom?: (anchorRatio: number, zoomFactor: number) => void
  onControllerReady?: (controller: PannableCanvasController | null) => void
  onTransformChange?: (transform: PannableCanvasTransform) => void
}) => {
  const viewportRef = useRef<HTMLDivElement | null>(null)
  const layerRef = useRef<HTMLDivElement | null>(null)
  const [overlayEl, setOverlayEl] = useState<HTMLDivElement | null>(null)
  const [viewportRect, setViewportRect] = useState<DOMRect | null>(null)
  const state = usePannableCanvasState({
    viewId,
    workspaceId,
    viewportRef,
    layerRef,
    pan,
    zoomAxis,
    zoomMode,
    onSemanticZoom,
  })

  const stateRef = useRef(state)
  useEffect(() => {
    stateRef.current = state
  })

  useEffect(() => {
    if (!onControllerReady) return
    const controller: PannableCanvasController = {
      zoomIn: () => stateRef.current.zoomIn(),
      zoomOut: () => stateRef.current.zoomOut(),
      fitToContent: () => stateRef.current.fitToContent(),
      getTransform: () => stateRef.current.transform,
    }
    onControllerReady(controller)
    return () => {
      onControllerReady(null)
    }
  }, [onControllerReady])

  useEffect(() => {
    onTransformChange?.(state.transform)
  }, [onTransformChange, state.transform])

  const measureViewport = useCallback(() => {
    const node = viewportRef.current
    if (!node) return
    setViewportRect(node.getBoundingClientRect())
  }, [])

  useEffect(() => {
    const node = viewportRef.current
    if (!node) return
    const handleWheel = (event: WheelEvent) => {
      const isZoomGesture = event.ctrlKey || event.metaKey
      if (isZoomGesture) {
        if (zoomMode === 'density' || zoomMode === 'none' || zoomAxis === 'none') {
          return
        }
        event.preventDefault()
        if (zoomMode === 'semantic') {
          const rect = viewportRef.current?.getBoundingClientRect()
          const width = Math.max(1, rect?.width ?? window.innerWidth)
          const anchorRatio = Math.min(1, Math.max(0, (event.clientX - (rect?.left ?? 0)) / width))
          const zoomFactor = Math.exp(event.deltaY * 0.0025)
          onSemanticZoom?.(anchorRatio, zoomFactor)
          return
        }
        // Zoom on ctrlKey / pinch-wheel (fixes RC-7)
        const factor = Math.pow(0.998, event.deltaY)
        state.zoomAt(event.clientX, event.clientY, factor)
        return
      }

      // Pan gesture via wheel
      if (pan === 'none') {
        return
      }

      const target = event.target instanceof Element ? event.target : null
      const nestedHorizontalScroll = target?.closest('[data-dashboard-horizontal-scroll="true"]')
      const isHorizontalMotion = Math.abs(event.deltaX) > Math.abs(event.deltaY)

      if (nestedHorizontalScroll && (isHorizontalMotion || (pan === 'y' && event.deltaX !== 0))) {
        // Nested horizontal scroll container owns horizontal wheel motion
        return
      }

      const canPanX = (pan === 'x' || pan === 'xy') && event.deltaX !== 0
      const canPanY = (pan === 'y' || pan === 'xy') && event.deltaY !== 0

      if (!canPanX && !canPanY) {
        return
      }

      event.preventDefault()
      const effectiveDx = canPanX ? -event.deltaX : 0
      const effectiveDy = canPanY ? -event.deltaY : 0
      state.panBy(effectiveDx, effectiveDy)
    }
    node.addEventListener('wheel', handleWheel, { passive: false })
    return () => node.removeEventListener('wheel', handleWheel)
  }, [onSemanticZoom, pan, state, zoomAxis, zoomMode])

  const reconcileDimensionsRef = useRef(state.reconcileDimensions)
  useEffect(() => {
    reconcileDimensionsRef.current = state.reconcileDimensions
  }, [state.reconcileDimensions])

  useEffect(() => {
    const node = viewportRef.current
    const layer = layerRef.current
    if (!node) return
    measureViewport()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      measureViewport()
      reconcileDimensionsRef.current()
    })
    observer.observe(node)
    if (layer) observer.observe(layer)
    return () => observer.disconnect()
  }, [measureViewport])

  const [edgeFades, setEdgeFades] = useState<EdgeFades>({ top: false, bottom: false, left: false, right: false })

  useEffect(() => {
    const layer = layerRef.current
    const viewport = viewportRef.current?.getBoundingClientRect() ?? viewportRect
    const bounds = layer ? measureLayerContentBounds(layer) : null
    const fades = computeEdgeFades(state.transform, bounds, viewport, zoomAxis, zoomMode)
    setEdgeFades((prev) => {
      if (
        prev.top === fades.top &&
        prev.bottom === fades.bottom &&
        prev.left === fades.left &&
        prev.right === fades.right
      ) {
        return prev
      }
      return fades
    })
  }, [state.transform, viewportRect, zoomAxis, zoomMode])

  const contextValue = useMemo(() => {
    const isSemantic = zoomMode === 'semantic'
    const scaleX = isSemantic || zoomAxis === 'y' || zoomAxis === 'none' ? 1 : state.transform.zoom
    const scaleY = isSemantic || zoomAxis === 'x' || zoomAxis === 'none' ? 1 : state.transform.zoom
    return {
      transform: state.transform,
      viewportRect,
      overlayEl,
      edgeFades,
      worldToScreen: (worldX: number, worldY: number) => {
        const vRect = viewportRef.current?.getBoundingClientRect() ?? viewportRect
        const originX = vRect?.left ?? 0
        const originY = vRect?.top ?? 0
        return {
          x: originX + state.transform.x + worldX * scaleX,
          y: originY + state.transform.y + worldY * scaleY,
        }
      },
      screenToWorld: (screenX: number, screenY: number) => {
        const vRect = viewportRef.current?.getBoundingClientRect() ?? viewportRect
        const originX = vRect?.left ?? 0
        const originY = vRect?.top ?? 0
        return {
          x: (screenX - originX - state.transform.x) / scaleX,
          y: (screenY - originY - state.transform.y) / scaleY,
        }
      },
      worldToViewport: (worldX: number, worldY: number) => {
        return {
          x: state.transform.x + worldX * scaleX,
          y: state.transform.y + worldY * scaleY,
        }
      },
      viewportToWorld: (viewportX: number, viewportY: number) => {
        return {
          x: (viewportX - state.transform.x) / scaleX,
          y: (viewportY - state.transform.y) / scaleY,
        }
      },
    }
  }, [edgeFades, overlayEl, state.transform, viewportRect, zoomAxis, zoomMode])

  const showZoom = zoomAxis !== 'none' && zoomMode !== 'none' && zoomMode !== 'density'

  return (
    <PannableCanvasContext.Provider value={contextValue}>
      <PannableCanvasControls
        showZoom={showZoom}
        onZoomIn={state.zoomIn}
        onZoomOut={state.zoomOut}
        onReset={state.fitToContent}
      />
      <div
        ref={viewportRef}
        className={`pannableCanvas${state.isPanning ? ' isPanning' : ''}`}
        data-pannable-canvas="true"
        style={{ cursor: state.isPanning ? 'grabbing' : pan !== 'none' ? 'grab' : undefined }}
        {...state.gestureHandlers}
      >
        <div
          ref={layerRef}
          className="pannableCanvas__layer"
          data-pannable-canvas-layer="true"
          style={{ transform: state.transformStyle, transition: state.isPanning ? 'none' : undefined }}
        >
          {children}
        </div>
        <div
          ref={setOverlayEl}
          className="pannableCanvas__overlay"
          data-pannable-canvas-overlay="true"
        />
        {edgeFades.top ? <div className="pannableCanvas__fade pannableCanvas__fade--top" data-testid="canvas-fade-top" aria-hidden="true" /> : null}
        {edgeFades.bottom ? <div className="pannableCanvas__fade pannableCanvas__fade--bottom" data-testid="canvas-fade-bottom" aria-hidden="true" /> : null}
        {edgeFades.left ? <div className="pannableCanvas__fade pannableCanvas__fade--left" data-testid="canvas-fade-left" aria-hidden="true" /> : null}
        {edgeFades.right ? <div className="pannableCanvas__fade pannableCanvas__fade--right" data-testid="canvas-fade-right" aria-hidden="true" /> : null}
      </div>
    </PannableCanvasContext.Provider>
  )
}
